import App from "./App";
import { OPEN_COOKIE_SETTINGS_EVENT } from "./CookieConsent";

interface Props {
  partner: string;
}

/**
 * Standalone landing served when the widget is opened directly
 * (e.g. wycena.halavach.pl from an ad click) — NOT inside a partner iframe.
 * Marketing copy lives here; the calculator card (App) is shared with embeds.
 */
export default function Landing({ partner }: Props) {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Hero — kept short so the calculator sits above the fold */}
      <header className="bg-brand text-white">
        <div className="max-w-3xl mx-auto px-6 pt-10 pb-8 md:pt-14 md:pb-10 text-center">
          <p className="text-sm uppercase tracking-widest opacity-70 mb-3">
            Halavach Real Estate
          </p>
          <h1 className="text-2xl md:text-4xl font-bold leading-tight">
            Poznaj realne widełki cenowe swojego mieszkania
          </h1>
          <p className="mt-3 text-lg opacity-90">
            <strong>Wynik zobaczysz od razu online.</strong> 2{"\u00A0"}minuty,
            bezpłatnie, bez zobowiązań.
          </p>
        </div>
      </header>

      {/* Kalkulator */}
      <section id="kalkulator" className="bg-brand-50 pt-8 pb-12 md:pt-10 md:pb-16">
        <div className="px-4 flex justify-center">
          <App partner={partner} />
        </div>
      </section>

      {/* Copy: błąd pierwszej ceny */}
      <section className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-brand">
          Błąd pierwszej ceny: jak nie stracić pieniędzy przy sprzedaży mieszkania
        </h2>
        <p className="mt-5 text-gray-700 leading-relaxed">
          Wystawiając nieruchomość na portale po zawyżonej cenie z myślą: „Zawsze
          zdążę obniżyć”, uruchamiasz niebezpieczny mechanizm.
        </p>
        <p className="mt-4 text-gray-700 leading-relaxed">
          Kupujący miesiącami monitorują rynek. Widząc, że ogłoszenie „wisi” zbyt
          długo, a cena stale spada, zaczynają podejrzewać ukrytą wadę:{" "}
          <em>„Z tym mieszkaniem musi być coś nie tak”</em>. W efekcie nieruchomość
          traci status „gorącej nowości”, a finalna kwota transakcji okazuje się
          niższa od realnej wartości rynkowej.
        </p>

        <h3 className="mt-10 text-xl md:text-2xl font-bold text-brand">
          Poznaj adekwatne widełki cenowe przed pierwszą publikacją
        </h3>
        <p className="mt-4 text-gray-700 leading-relaxed">
          Kalkulator online, zasilany algorytmem sztucznej inteligencji, w 2 minuty
          analizuje zamknięte transakcje w Warszawie i generuje obiektywne widełki
          cenowe dla Twojej nieruchomości.
        </p>

        <p className="mt-6 font-medium text-gray-900">Co system bierze pod uwagę:</p>
        <ul className="mt-3 space-y-2 text-gray-700">
          <li className="flex gap-3">
            <span className="text-brand font-bold">✓</span>
            dokładną lokalizację (dzielnicę, ulicę, inwestycję), metraż i standard
            wykończenia,
          </li>
          <li className="flex gap-3">
            <span className="text-brand font-bold">✓</span>
            aktualne ceny transakcyjne podobnych mieszkań w tej okolicy.
          </li>
        </ul>

        <p className="mt-8 text-lg font-semibold text-brand">
          Zabezpiecz się przed utratą zainteresowania kupujących i twardymi
          negocjacjami.
        </p>

        <a
          href="#kalkulator"
          className="inline-block mt-8 px-8 py-4 bg-brand text-white rounded-xl font-semibold hover:bg-brand-600 transition"
        >
          Wyceń mieszkanie
        </a>
      </section>

      {/* Stopka */}
      <footer className="bg-brand text-white">
        <div className="max-w-3xl mx-auto px-6 py-8 text-sm text-center space-y-3">
          <div className="space-x-4">
            <a href="/polityka-prywatnosci" className="underline opacity-80 hover:opacity-100">
              Polityka prywatności
            </a>
            <a href="/polityka-cookies" className="underline opacity-80 hover:opacity-100">
              Polityka plików cookie
            </a>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT))}
              className="underline opacity-80 hover:opacity-100"
            >
              Ustawienia plików cookie
            </button>
          </div>
          <p className="opacity-60">
            Wycena ma charakter orientacyjny i nie stanowi operatu szacunkowego.
          </p>
          <p className="opacity-60">
            © {new Date().getFullYear()} HALAVACH Real Estate Agency
          </p>
        </div>
      </footer>
    </div>
  );
}
